import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getPromoterDashboard } from "../../services/eventService";
import { downloadMediaFile } from "../../services/uploadService";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import { toast } from "react-toastify";

const statusColor = {
  Upcoming: { bg: "#ede9ff", text: "#6c63ff" },
  Live: { bg: "#dcfce7", text: "#16a34a" },
  Closed: { bg: "#f3f4f6", text: "#6b7280" },
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    getPromoterDashboard({ signal: controller.signal })
      .then((res) => setData(res.data?.Result || res.data))
      .catch((err) => {
        if (!controller.signal.aborted) toast.error("Failed to load dashboard.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  if (loading) return <Loader />;
  if (!data) return <p style={{ color: "#888", padding: 20 }}>Could not load dashboard data.</p>;

  const stats = [
    { label: "Total Events", value: data.TotalEvents ?? 0, icon: "🎉", color: "#6c63ff" },
    { label: "Active Events", value: data.ActiveEvents ?? 0, icon: "🔴", color: "#16a34a" },
    { label: "Total Participants", value: data.TotalParticipants ?? 0, icon: "👥", color: "#f59e0b" },
    { label: "Total Media", value: data.TotalMedia ?? 0, icon: "📸", color: "#e53935" },
  ];

  const recentEvents = data.RecentEvents || [];
  const recentMedia = data.RecentMedia || [];
  const recentActivities = data.RecentActivities || [];

  const getDownloadUrl = (media) => {
    if (!media) return null;
    if (media.VideoURL) return media.VideoURL;
    return media.Url || null;
  };

  const getDownloadName = (media) => {
    const source = getDownloadUrl(media) || "";
    const sourceName = source.split("?")[0].split("/").pop();
    if (sourceName) return sourceName;
    const ext = media?.VideoURL ? "mp4" : "jpg";
    return `media-${media?.Id || "file"}.${ext}`;
  };

  const downloadMedia = async (media) => {
    const url = getDownloadUrl(media);
    if (!url) {
      toast.error("Download URL not available.");
      return;
    }

    try {
      await downloadMediaFile({
        url,
        fileName: getDownloadName(media),
      });
    } catch {
      toast.error("Download failed. Please make sure Client app is running.");
    }
  };

  return (
    <div>
      {/* Header */}
      <h2 style={s.heading}>
        Welcome back, {user?.Name || user?.UserName || user?.Email || "Promoter"} 👋
      </h2>
      <p style={s.sub}>Here's an overview of your panel.</p>

      {/* ─── Top Stats ─── */}
      <div style={s.statsGrid}>
        {stats.map((st) => (
          <div key={st.label} style={s.statCard}>
            <span style={{ ...s.statIcon, background: st.color + "18", color: st.color }}>{st.icon}</span>
            <div>
              <div style={{ ...s.statValue, color: st.color }}>{st.value}</div>
              <div style={s.statLabel}>{st.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ─── Event Performance ─── */}
      <div style={s.section}>
        <div style={s.sectionHeader}>
          <h3 style={s.sectionTitle}>🔥 Event Performance</h3>
          {recentEvents.length > 0 && (
            <span style={s.sectionSub}>Last {recentEvents.length} events</span>
          )}
        </div>
        {recentEvents.length === 0 ? (
          <p style={s.empty}>No events yet.</p>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Event Name</th>
                  <th style={s.th}>Participants</th>
                  <th style={s.th}>Media</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Created</th>
                </tr>
              </thead>
              <tbody>
                {recentEvents.map((evt) => {
                  const sc = statusColor[evt.Status] || statusColor.Closed;
                  return (
                    <tr
                      key={evt.EventId}
                      style={s.tr}
                      onClick={() => evt.EventId && navigate(`/events/${evt.EventId}`)}
                    >
                      <td style={s.td}>
                        <span style={s.evtName}>{evt.Name}</span>
                      </td>
                      <td style={s.td}>{evt.Participants ?? 0}</td>
                      <td style={s.td}>{evt.MediaCount ?? 0}</td>
                      <td style={s.td}>
                        {evt.Status ? (
                          <span style={{ ...s.statusBadge, background: sc.bg, color: sc.text }}>
                            {evt.Status}
                          </span>
                        ) : (
                          <span style={{ ...s.statusBadge, background: "#f3f4f6", color: "#6b7280" }}>—</span>
                        )}
                      </td>
                      <td style={s.td}>{evt.CreatedAt ? new Date(evt.CreatedAt).toLocaleDateString() : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Bottom Row: Live Activity + Recent Media ─── */}
      <div style={s.bottomRow}>
        {/* Live Activity */}
        <div style={{ ...s.section, flex: 1, minWidth: 280 }}>
          <h3 style={s.sectionTitle}>⚡ Live Activity</h3>
          <div style={s.activityList}>
            {recentActivities.length > 0 ? (
              recentActivities.slice(0, 8).map((act, i) => (
                <div key={i} style={s.activityItem}>
                  <span style={s.activityDot} />
                  <span style={s.activityText}>{act.Message || act.Description || JSON.stringify(act)}</span>
                </div>
              ))
            ) : (
              <p style={s.empty}>No recent activity.</p>
            )}
            <p style={s.hint}>🔌 Real-time updates via SignalR coming soon</p>
          </div>
        </div>

        {/* Recent Media */}
        <div style={{ ...s.section, flex: 1, minWidth: 280 }}>
          <h3 style={s.sectionTitle}>🖼️ Recent Media</h3>
          <div style={s.mediaList}>
            {recentMedia.length > 0 ? (
              recentMedia.slice(0, 5).map((m, i) => (
                <div key={i} style={s.mediaItem}>
                  <img
                    src={m.Url}
                    alt="media"
                    style={s.mediaThumbnail}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                  <div style={s.mediaInfo}>
                    <span style={s.mediaEvent}>{m.EventName || "—"}</span>
                    <span style={s.mediaBy}>by {m.UploadedBy || "Unknown"}</span>
                  </div>
                  <button
                    style={s.mediaDownloadBtn}
                    onClick={() => downloadMedia(m)}
                    title="Download"
                  >
                    ⬇
                  </button>
                  <span style={s.mediaDate}>{m.CreatedAt ? new Date(m.CreatedAt).toLocaleDateString() : ""}</span>
                </div>
              ))
            ) : (
              <p style={s.empty}>No recent uploads.</p>
            )}
          </div>
        </div>
      </div>

      {/* ─── Action Required ─── */}
      <div style={s.section}>
        <h3 style={s.sectionTitle}>⚠️ Action Required</h3>
        <div style={s.activityList}>
          <div style={s.placeholderItem}>
            <span style={s.placeholderIcon}>📋</span>
            <span style={s.placeholderText}>Pending approvals</span>
            <span style={s.placeholderBadge}>—</span>
          </div>
          <div style={s.placeholderItem}>
            <span style={s.placeholderIcon}>🚩</span>
            <span style={s.placeholderText}>Flagged content</span>
            <span style={s.placeholderBadge}>—</span>
          </div>
          <div style={s.placeholderItem}>
            <span style={s.placeholderIcon}>✅</span>
            <span style={s.placeholderText}>Verification requests</span>
            <span style={s.placeholderBadge}>—</span>
          </div>
          <p style={s.hint}>Will be connected to backend APIs</p>
        </div>
      </div>

      {/* ─── Quick Actions ─── */}
      <div style={s.section}>
        <h3 style={s.sectionTitle}>🚀 Quick Actions</h3>
        <div style={s.quickActions}>
          <Button onClick={() => navigate("/events")} style={s.actionBtn}>
            🎉 View All Events
          </Button>
          <Button onClick={() => navigate("/rooms")} style={s.actionBtn}>
            💬 Open Chat Rooms
          </Button>
        </div>
      </div>
    </div>
  );
};

const s = {
  heading: { margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#1e1e2d" },
  sub: { margin: "0 0 28px", color: "#888", fontSize: 14 },

  // Stats
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 },
  statCard: {
    background: "#fff", borderRadius: 14, padding: "18px 20px",
    display: "flex", alignItems: "center", gap: 14,
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  },
  statIcon: {
    fontSize: 22, width: 44, height: 44, borderRadius: 10,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  statValue: { fontSize: 24, fontWeight: 700, lineHeight: 1.1 },
  statLabel: { fontSize: 11, color: "#888", marginTop: 3, whiteSpace: "nowrap" },

  // Sections
  section: {
    background: "#fff", borderRadius: 14, padding: "20px 24px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 24,
  },
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  sectionTitle: { margin: 0, fontSize: 16, fontWeight: 700, color: "#1e1e2d" },
  sectionSub: { fontSize: 12, color: "#888", background: "#f5f5f5", padding: "3px 10px", borderRadius: 20 },

  // Table
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 600,
    color: "#888", textTransform: "uppercase", letterSpacing: 0.5,
    borderBottom: "2px solid #f0f0f0", whiteSpace: "nowrap",
  },
  tr: { cursor: "pointer", transition: "background 0.1s" },
  td: { padding: "12px 14px", borderBottom: "1px solid #f5f5f5", color: "#444", whiteSpace: "nowrap" },
  evtName: { fontWeight: 600, color: "#1e1e2d" },
  typeBadge: {
    background: "#ede9ff", color: "#6c63ff", fontSize: 10, fontWeight: 600,
    padding: "2px 8px", borderRadius: 20, textTransform: "uppercase",
  },
  statusBadge: {
    fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, whiteSpace: "nowrap",
  },

  // Bottom row
  bottomRow: { display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 0 },

  // Activity
  activityList: { display: "flex", flexDirection: "column", gap: 10, marginTop: 12 },
  activityItem: { display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#444" },
  activityDot: {
    width: 8, height: 8, borderRadius: "50%", background: "#16a34a", flexShrink: 0,
  },
  activityText: { fontSize: 13 },
  hint: { fontSize: 11, color: "#aaa", fontStyle: "italic", margin: "10px 0 0" },

  // Action required placeholders
  placeholderItem: {
    display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#444",
    padding: "8px 0", borderBottom: "1px solid #f5f5f5",
  },
  placeholderIcon: { fontSize: 16, width: 24, textAlign: "center" },
  placeholderText: { flex: 1 },
  placeholderBadge: { fontSize: 12, color: "#aaa", fontWeight: 600 },

  // Quick actions
  quickActions: { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12 },
  actionBtn: { padding: "10px 20px", fontSize: 13 },

  // Recent Media
  mediaList: { display: "flex", flexDirection: "column", gap: 10, marginTop: 12 },
  mediaItem: {
    display: "flex", alignItems: "center", gap: 12, fontSize: 13, color: "#444",
    padding: "8px 0", borderBottom: "1px solid #f5f5f5",
  },
  mediaThumbnail: {
    width: 44, height: 44, borderRadius: 8, objectFit: "cover", flexShrink: 0,
    background: "#f0f0f0",
  },
  mediaInfo: { flex: 1, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
  mediaEvent: { fontWeight: 600, color: "#1e1e2d", fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  mediaBy: { fontSize: 11, color: "#888" },
  mediaDownloadBtn: {
    border: "1px solid #d8dbe3",
    background: "#fff",
    color: "#444",
    borderRadius: 6,
    width: 28,
    height: 28,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    flexShrink: 0,
  },
  mediaDate: { fontSize: 11, color: "#aaa", flexShrink: 0, whiteSpace: "nowrap" },

  empty: { color: "#888", fontSize: 13 },
};

export default Dashboard;
